import React, { memo, useMemo, useCallback } from 'react';
import { Container, Card, CardBody } from 'reactstrap';
import { AssistantButton, PageHeader } from '@modules/common';
import { useBloc } from '@core/utils/bloc';
import { ProjectChecklistResponse } from '@business/entities/project';
import { useRouteParam } from '@core/router';
import { SortableList } from '@modules/common/Sortable';
import { CheckListDetailPageBloc } from './CheckListDetailPageBloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { R } from '@core/utils/r';
import { SortEnd } from 'react-sortable-hoc';
import { SortableChecklistSectionItem } from './CheckListSectionItem';
import { openAddEditChecklistSection } from './AddEditChecklistSectionModal';
import { TopLoadingBar } from '@core/components/LoadingBar';
import OpenAI from 'openai';
import { Observable, of } from 'rxjs';

export const CheckListDetailPage = memo(() => {
  const checklistId = useRouteParam('id');
  const bloc = useBloc(CheckListDetailPageBloc, checklistId);
  const checklist = useObservable(bloc.template$) as ProjectChecklistResponse | null;
  const loading = useLoadingState(bloc.loading);

  const onAIOutput = useCallback(
    (output: string) => {
      try {
        const result = JSON.parse(output);
        console.log('result:', result);
        bloc.createOrUpdate(result.id, result).subscribe();
      } catch (e) {
        console.log('Error parsing AI Course outline output:', e);
        return;
      }
    },
    [bloc],
  );

  const createOrUpdate = useCallback((callId: string, args: any) => {
    const { id } = args;
    const payload: Partial<ProjectChecklistResponse> = {
      id,
      name: args.name,
      description: args.description,
      sections: (args.sections ?? []).map((section: any) => ({
        id: section.id,
        name: section.name,
        steps: section.steps.map((step: any) => ({
          id: step.id,
          name: step.name,
        })),
      })),
    };

    return of({
      tool_call_id: callId,
      output: JSON.stringify(payload),
    });
  }, []);

  const getTemplateDetails = useCallback(
    (callId: string, args: any) => {
      const data = {
        id: checklist.id,
        name: checklist.name,
        description: checklist.description,
        sections: checklist.sections.map(section => ({
          id: section.id,
          name: section.name,
          steps: section.steps.map(step => ({
            id: step.id,
            name: step.name,
          })),
        })),
      };
      return of({ tool_call_id: callId, output: JSON.stringify(data) });
    },
    [checklist],
  );

  const onToolCall = useCallback(
    (callId: string, fnName: string, args: any) => {
      const functionMap: { [key: string]: (callId: string, args: any) => Observable<any> } = {
        getTemplateDetails,
        createOrUpdate,
      };
      const argsT = JSON.parse(args);
      if (functionMap[fnName]) {
        return functionMap[fnName](callId, argsT) as any;
      } else {
        console.error(`Function ${fnName} not found`);
      }
      return of(null);
    },
    [createOrUpdate, getTemplateDetails],
  );

  const tools: OpenAI.Responses.Tool[] = useMemo(() => {
    return [
      {
        type: 'function',
        name: 'getTemplateDetails',
        description: 'Fetches the current template and existing section/step and subStep structure. Must be used before any other action.',
        strict: false,
        parameters: {},
      },
      {
        type: 'function',
        name: 'createOrUpdate',
        description: 'Create/Update sections and steps for the course and returns the courseId and sections with steps',
        strict: true,
        parameters: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            name: { type: 'string' },
            description: { type: 'string' },
            sections: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  name: { type: 'string' },
                  steps: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        id: { type: 'string' },
                        name: { type: 'string' },
                        instructions: { type: 'string' },
                      },
                      required: ['id', 'name', 'instructions'],
                      additionalProperties: false,
                    },
                  },
                },
                required: ['id', 'name', 'steps'],
                additionalProperties: false,
              },
              additionalProperties: false,
            },
          },
          required: ['id', 'name', 'description', 'sections'],
          additionalProperties: false,
        },
      },
    ];
  }, []);

  const buttons = useMemo(() => {
    return [
      {
        children: (
          <div className="mr-2">
            <AssistantButton assistantId={'35'} tools={tools} onToolCall={onToolCall} onDone={onAIOutput}>
              Generate with AI
            </AssistantButton>
          </div>
        ),
      },
      {
        title: 'Add Section',
        onClick: () => openAddEditChecklistSection({ checklistId }),
      },
    ];
  }, [checklistId, onAIOutput, onToolCall, tools]);

  const onDelete = useCallback(
    (id: number) => {
      bloc.deleteSection(checklistId, id).subscribe();
    },
    [bloc, checklistId],
  );

  const onStepDelete = useCallback(
    (id: number) => {
      bloc.deleteSectionStep(checklistId, id).subscribe();
    },
    [bloc, checklistId],
  );

  const onSectionSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const sectionIds = checklist!.sections.map(s => s.id);
      const newSectionIds = R.move(sectionIds, oldIndex, newIndex);
      bloc.sortSections(checklistId, newSectionIds).subscribe();
    },
    [bloc, checklist, checklistId],
  );

  const onStepSortEnd = useCallback(
    (sectionId: number, sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;

      const section = R.find(checklist!.sections, s => s.id === sectionId)!;

      const stepIds = section.steps.map(s => s.id);
      const newStepIds = R.move(stepIds, oldIndex, newIndex);
      bloc.sortSectionSteps(checklistId, sectionId, newStepIds).subscribe();
    },
    [checklist, bloc, checklistId],
  );

  if (loading.loading || !checklist) return <TopLoadingBar />;
  const sections = checklist.sections;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>{`Template Settings of ${checklist.name}`}</PageHeader>
        <Card>
          <CardBody>
            <SortableList lockAxis="y" distance={2} onSortEnd={onSectionSortEnd}>
              {sections.map((section, sectionIndex) => {
                const steps = section.steps;
                return (
                  <SortableChecklistSectionItem
                    section={section}
                    steps={steps}
                    index={sectionIndex}
                    key={sectionIndex}
                    onDelete={onDelete}
                    checklistId={checklistId}
                    onStepSortEnd={onStepSortEnd}
                    onStepDelete={onStepDelete}
                  />
                );
              })}
            </SortableList>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
});
